/**
 * Dimensions — co-located API client (P5 Wave 4 §4).
 *
 * Calls the `buildDimensionsRouter(pool)` endpoints:
 *   GET   /                 → { success, dimensions: [...] }
 *   PATCH /:dimension_key   → { success, dimension }
 *     body { decay_half_life_days?, importance?, is_charter_sensitive?,
 *            is_internal_only?, reason, confirm_reveal? }
 *
 * DECISION 7.4 REVEAL GUARD: flipping `is_internal_only` TRUE→FALSE (revealing a
 * previously internal-only dimension) requires `confirm_reveal: <dimension_key>`
 * in the body; without it the backend replies 428 REVEAL_CONFIRM_REQUIRED.
 * Turning internal-only ON (FALSE→TRUE) is free. The page enforces the typed-
 * name step client-side and always sends `confirm_reveal`, and still handles a
 * 428 defensively (surfaced via QuillApiError.code).
 *
 * The `request` helper is replicated from `quillAdminService.ts` (which does not
 * export it) — same fetch/proxy seam and `{success:false}`-is-an-error + `code`
 * parsing semantics. BASE_SEG is the router mount segment wired by the
 * orchestrator (server.js); keep it in sync at merge.
 */

import { QuillApiError } from '../../../services/quillAdminService';

const BASE = '/api/admin/quill';
const BASE_SEG = '/dimensions';

interface EnvelopeShape {
  success?: boolean;
  code?: string;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    throw new QuillApiError(
      e instanceof Error ? e.message : 'Network error contacting Quill backend',
      0,
      'NETWORK_ERROR'
    );
  }

  let body: EnvelopeShape | null = null;
  try {
    body = await res.json();
  } catch {
    // No/invalid JSON body — handled below.
  }

  if (!res.ok || (body && body.success === false)) {
    const message = body?.message || body?.error || `Quill backend request failed (${res.status})`;
    throw new QuillApiError(message, res.status, body?.code);
  }

  return (body ?? {}) as T;
}

// ── Types ───────────────────────────────────────────────────────────────

export interface PersonalityDimension {
  dimension_key: string;
  display_name: string;
  /** Decay half-life (τ) in days. Range 30..3650. */
  decay_half_life_days: number;
  /** Relative weight of this dimension. 0..1. */
  importance: number;
  is_charter_sensitive: boolean;
  is_internal_only: boolean;
  /**
   * F4: this dimension can NEVER be made member-visible, whatever the column
   * says. quill-engine unions this table with a hardcoded internal-only set
   * (migration 518 / decision 7.4 — quill_coping_profile has no member surface
   * and must never appear in one), so the flag is a floor, not a switch.
   * Derived by the backend from code, never stored. The page renders these rows
   * without a reveal control, and PATCHing is_internal_only=false for one is
   * refused with 409 DIMENSION_PERMANENTLY_INTERNAL.
   *
   * Optional so an older backend (which does not send it) simply behaves as
   * before rather than crashing the table.
   */
  is_permanently_internal?: boolean;
}

export interface UpdateDimensionRequest {
  /**
   * τ in quill-engine's evidence-decay curve. Whole days, 30..3650 — see
   * `dimensionBounds.ts`; the route answers 400 DIMENSION_BOUNDS otherwise.
   */
  decay_half_life_days?: number;
  /**
   * The interview-ranking multiplier. 0.01..1.00 at two decimal places. NOT 0:
   * zero silences the dimension's questions entirely rather than weighting them
   * down, and the route refuses it.
   */
  importance?: number;
  is_charter_sensitive?: boolean;
  is_internal_only?: boolean;
  reason: string;
  /** Required (must equal dimension_key) when revealing an internal-only
   *  dimension (is_internal_only TRUE→FALSE). */
  confirm_reveal?: string;
  /**
   * Required (must equal dimension_key) when CLEARING charter-sensitive
   * (is_charter_sensitive TRUE→FALSE) — that removes the gate keeping
   * unconfirmed claims on the dimension out of what a member's Quill says to
   * their family. Without it the backend replies 428
   * CHARTER_SENSITIVE_CONFIRM_REQUIRED. Setting it is free.
   */
  confirm_charter_sensitive?: string;
}

// ── Client ──────────────────────────────────────────────────────────────

export const dimensionsApi = {
  /** GET / — all personality dimensions. */
  async getDimensions(): Promise<PersonalityDimension[]> {
    const body = await request<{
      dimensions?: PersonalityDimension[];
      data?: PersonalityDimension[];
    }>(`${BASE_SEG}/`);
    return body.dimensions ?? body.data ?? [];
  },

  /** PATCH /:dimension_key — edit a dimension. See the reveal-guard note above. */
  async updateDimension(
    dimensionKey: string,
    payload: UpdateDimensionRequest
  ): Promise<PersonalityDimension | undefined> {
    const body = await request<{ dimension?: PersonalityDimension } & PersonalityDimension>(
      `${BASE_SEG}/${encodeURIComponent(dimensionKey)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }
    );
    return body.dimension ?? (body as PersonalityDimension);
  },
};

export default dimensionsApi;
